import { useCapability } from '../../../hooks/use-capability';

import type { ConversationListItem } from '../../../lib/types';

/**
 * MetaSessionBlock — r7b §34 ADR-127 pixel-fiel.
 *
 * Bloco de MetaSession na aba "Conversa": sessão WhatsApp Business atual,
 * tipo de sessão (6 tipos ADR-127), status da integração Meta (normal /
 * degraded / down) e referência ADR-127.
 *
 * Capability gate: `multi-channel-extended` (Team+). Sem capability →
 * placeholder inline.
 *
 * Estado degradado (r7b §34.2 — `metaIntegrationStatus`):
 *   - `normal`   → sem banner (padrão)
 *   - `degraded` → banner warning com borda-esq laranja (recuperação é automática;
 *     botão "Tentar resincronizar" do gabarito removido — sem endpoint de resync no Engine)
 *   - `down`     → banner danger com borda-esq vermelha + composer continua
 *
 * Tipos de sessão canônicos (META_SESSION_TYPES shared — ADR-127):
 *   - `customer_care`   → "Janela de atendimento (24h)"
 *   - `service`         → "Atendimento ao cliente"
 *   - `ctwa`            → "Anúncio click-to-WhatsApp"
 *   - `authentication`  → "Autenticação"
 *   - `utility`         → "Utilitário"
 *   - `marketing`       → "Marketing"
 *   - null              → sem sessão ativa
 */
export function MetaSessionBlock({
  conversation,
}: {
  conversation: Pick<
    ConversationListItem,
    'metaSessionType' | 'metaSessionIsExpired' | 'channel'
  > & {
    metaIntegrationStatus?: 'normal' | 'degraded' | 'down';
  };
}): JSX.Element | null {
  const hasCapability = useCapability('multi-channel-extended');

  // Só aplica para conversas WhatsApp Cloud API.
  if (conversation.channel !== 'whatsapp-cloud-api') return null;

  if (!hasCapability) {
    return (
      <div className="rounded border border-border-subtle bg-surface-raised px-3 py-2 text-xs text-text-secondary">
        <span className="font-medium text-text-primary">Sessão Meta</span>
        <span className="mx-2 opacity-50">·</span>
        <span className="opacity-60">Disponível a partir do pacote Team.</span>
      </div>
    );
  }

  const integrationStatus = conversation.metaIntegrationStatus ?? 'normal';

  // Estado "down" — exibe apenas banner de indisponibilidade (r7b §34.2).
  if (integrationStatus === 'down') {
    return (
      <div
        className="meta-session-block__degraded--down space-y-1"
        role="alert"
        data-testid="meta-session-block"
      >
        <p className="text-xs font-semibold text-danger-fg">Meta API indisponível</p>
        <p className="text-xs text-text-secondary">
          Atendente continua compondo · envios enfileirados até API voltar. Sessões e custos serão
          recalculados quando integração retornar.
        </p>
      </div>
    );
  }

  // Sem sessão ativa — empty state honesto (antes retornava null e a seção
  // "Sessão WhatsApp" ficava visualmente vazia).
  if (conversation.metaSessionType === null) {
    return (
      <p className="text-xs text-text-secondary opacity-70 italic" data-testid="meta-session-empty">
        Sem sessão ativa no momento.
      </p>
    );
  }

  const label = SESSION_LABELS[conversation.metaSessionType] ?? conversation.metaSessionType;
  const isExpired = conversation.metaSessionIsExpired === true;

  return (
    <div
      className="rounded border border-border-subtle bg-surface px-3 py-2 space-y-2"
      data-testid="meta-session-block"
    >
      {/* Banner degradado (r7b §34.2) */}
      {integrationStatus === 'degraded' && (
        <div
          className="meta-session-block__degraded--degraded space-y-1"
          role="alert"
          data-testid="meta-degraded-banner"
        >
          <p className="text-xs font-semibold text-warning-fg">
            Status da integração Meta: degradado
          </p>
          <p className="text-xs text-text-secondary">
            Última sincronização atrasada · envios podem demorar mais que o normal. A sincronização
            é reestabelecida automaticamente.
          </p>
        </div>
      )}

      {/* Sessão atual */}
      <div className="rounded bg-success-bg border border-success-border px-2 py-1.5">
        <div className="flex items-center justify-between gap-2">
          <span className="text-2xs uppercase tracking-wide font-semibold text-text-secondary">
            Sessão Meta
          </span>
          <span
            className={`text-2xs font-mono rounded px-1 py-0.5 ${
              isExpired
                ? 'bg-danger-bg text-danger-fg border border-danger-border'
                : 'bg-success-bg text-success-fg border border-success-border'
            }`}
            title={isExpired ? 'Sessão expirada — janela 24h encerrada' : 'Sessão ativa'}
          >
            {isExpired ? 'expirada' : 'ativa'}
          </span>
        </div>
        <div className="flex items-center gap-2 text-sm mt-1">
          <span className="text-text-primary font-medium">{label}</span>
        </div>
      </div>
    </div>
  );
}

/** Labels canônicos dos 6 tipos de sessão Meta (ADR-127) — reusado pelo IdentityPopover. */
export const SESSION_LABELS: Record<string, string> = {
  customer_care: 'Janela de atendimento (24h)',
  service: 'Atendimento ao cliente',
  ctwa: 'Anúncio click-to-WhatsApp',
  authentication: 'Autenticação',
  utility: 'Utilitário',
  marketing: 'Marketing',
};
